/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped resource: responses are passed through as the server sends them. */
import { SdkContext } from '../internal'
import { withAnalyticsSession } from '@/lib/analytics/session'
import type { CommunityOverview, LedgerBalance, Payout } from '@/types/monetization'

export interface ProductPageParams {
  type?: 'ONE_TIME' | 'SUBSCRIPTION'
  status?: string
  kind?: string
  search?: string
  includeInactive?: boolean
  page?: number
  limit?: number
}

export interface ProductPage {
  items: any[]
  pagination: { page: number; limit: number; total: number; totalPages: number }
}

/** A listing the shared client may hand back bare or normalized to `{ items, pagination }` (paged API routes). */
export type MaybePaged<T> = T[] | { items: T[]; pagination?: unknown }

export class ProductsResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: Record<string, any>): Promise<any[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/products' },
      { query: params }
    )
  }

  /**
   * One page of the listing with its pagination (APP-113). `search` matches
   * title and slug (internalName too for the team), ignoring case and accents;
   * `limit` caps at 50.
   */
  async page(params: ProductPageParams = {}): Promise<ProductPage> {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    )
    const result = await this.ctx.raw<any>({ httpMethod: 'GET', path: '/products' }, { query })
    const items = Array.isArray(result) ? result : result?.items || result?.data || []
    const pagination = result?.pagination || { page: 1, limit: items.length, total: items.length, totalPages: 1 }
    return { items, pagination }
  }

  async get(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/products/{id}' },
      { id }
    )
  }

  async create(product: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/products', hasBody: true },
      { body: product }
    )
  }

  async update(id: string, product: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/products/{id}', hasBody: true },
      { id, body: product }
    )
  }

  /**
   * DRAFT -> ACTIVE. 403 until the business information is approved. The first
   * publication of a store product answers `emailNotice` (F-104; `notifyByEmail` omitted = on).
   */
  async publish(id: string, notifyByEmail?: boolean): Promise<unknown> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/products/{id}/publish', hasBody: true },
      { id, body: notifyByEmail === undefined ? {} : { notifyByEmail } }
    )
  }

  /** ACTIVE -> out of sale. Whoever already holds it keeps access (FIN-40). */
  async unpublish(id: string): Promise<unknown> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/products/{id}/unpublish' },
      { id }
    )
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/products/{id}' },
      { id }
    )
  }

  /** F-74: joins the waitlist of a sold-out product. 409 while there is a free place. */
  async joinWaitlist(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'POST', path: '/products/{id}/waitlist' }, { id })
  }

  /** F-74: leaves the waitlist; an open offer goes to the next person. */
  async leaveWaitlist(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/products/{id}/waitlist' }, { id })
  }

  /** F-74: the whole queue, for the team. */
  async waitlist(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/products/{id}/waitlist' }, { id })
  }

  /** F-74: the caller's own place, or null when not in the queue. */
  async myWaitlistPlace(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/products/{id}/waitlist/me' }, { id })
  }
}

export class CouponsResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: Record<string, any>): Promise<MaybePaged<any>> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/coupons' },
      { query: params }
    )
  }

  async get(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/coupons/{id}' },
      { id }
    )
  }

  async create(coupon: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/coupons', hasBody: true },
      { body: coupon }
    )
  }

  async update(id: string, coupon: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/coupons/{id}', hasBody: true },
      { id, body: coupon }
    )
  }

  async delete(id: string): Promise<{ id: string }> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/coupons/{id}' },
      { id }
    )
  }

  async validate(params: {
    code: string
    productId: string
    amount: number
  }): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/coupons/validate', hasBody: true },
      { body: params }
    )
  }
}

export class SubscriptionGroupsResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: Record<string, any>): Promise<MaybePaged<any>> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/subscription-groups' },
      { query: params }
    )
  }

  async get(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/subscription-groups/{id}' },
      { id }
    )
  }

  async create(group: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/subscription-groups', hasBody: true },
      { body: group }
    )
  }

  async update(id: string, group: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/subscription-groups/{id}', hasBody: true },
      { id, body: group }
    )
  }

  async delete(id: string): Promise<{ id: string }> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: '/subscription-groups/{id}' },
      { id }
    )
  }

  async reorder(groupIds: string[]): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'PATCH', path: '/subscription-groups/reorder', hasBody: true },
      { body: { groupIds } }
    )
  }
}

export class CheckoutResource {
  constructor(private ctx: SdkContext) {}

  async getConfig(): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/checkout/config' },
      {}
    )
  }

  async calculatePrice(params: {
    productId: string
    paymentMethod: string
    installments: number
    /** F-37: add-ons bought with the plan. */
    addOnProductIds?: string[]
    /** FIN-7: a coupon to apply; refusals come back with `errors[0].param = couponCode`. */
    couponCode?: string
    /** F-93 1B: priced for the company (no trial; its offers only). */
    companyId?: string
  }): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/checkout/calculate-price', hasBody: true },
      { body: params }
    )
  }

  async create(checkoutData: any): Promise<any> {
    return this.ctx.call({ httpMethod: 'POST', path: '/checkout', hasBody: true }, { body: withAnalyticsSession(checkoutData ?? {}) })
  }

  /** The signed-in member's product purchases, newest first (F-74: "My purchases"). */
  async myPurchases(params: { page?: number; limit?: number; status?: string } = {}): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/checkout/purchases' }, { query: params })
  }

  /** One of the signed-in buyer's purchases; used to follow a PIX until it is paid. */
  /**
   * Asks the API to check the purchase with the provider (at most once every
   * 10 s per purchase) and answers the current state: `outcome` is paid,
   * failed, pending, unchanged, throttled or skipped.
   */
  async refreshPurchase(purchaseId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/checkout/purchases/{purchaseId}/refresh', hasBody: true },
      { purchaseId, body: {} }
    )
  }

  /**
   * Closes an unpaid PENDING/PROCESSING purchase as FAILED. 409 when the
   * provider says it was paid meanwhile; 502 when the provider is down.
   */
  async cancelPurchase(purchaseId: string): Promise<{ id?: string; status?: string } | null> {
    return this.ctx.call({ httpMethod: 'POST', path: '/checkout/{purchaseId}/cancel', hasBody: true }, { purchaseId, body: {} })
  }

  async getPurchase(purchaseId: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: '/checkout/{purchaseId}' }, { purchaseId })
  }

  async getSubscriptionGroups(): Promise<any[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/checkout/subscription-groups' },
      {}
    )
  }

  /** F-29 phase 3: one plan by its slug, whatever its visibility (`link_only` opens only by link). */
  async getSubscriptionGroup(slug: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/checkout/subscription-groups/{slug}' },
      { slug }
    )
  }

  async getSubscriptions(): Promise<any[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/checkout/subscriptions' },
      {}
    )
  }

  async getProducts(): Promise<any[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/checkout/products' },
      {}
    )
  }
}

export class LedgerResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: Record<string, any>): Promise<MaybePaged<any>> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/ledger' },
      { query: params }
    )
  }

  /** F-56: `currency` picks the balance's currency (BRL by default); `balances` always has one per currency. */
  async getBalance(params?: { currency?: string }): Promise<LedgerBalance> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/ledger/balance' },
      { query: params }
    )
  }
}

export class ReportsResource {
  constructor(private ctx: SdkContext) {}

  /** F-46 overview: this month and last; `money.byCurrency` has one line per currency (F-56). */
  async overview(params?: { timezone?: string }): Promise<CommunityOverview> {
    return this.ctx.call({ httpMethod: 'GET', path: '/reports/overview' }, { query: params })
  }
}

export class PayoutsResource {
  constructor(private ctx: SdkContext) {}

  async list(params?: Record<string, any>): Promise<any[]> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/payouts' },
      { query: params }
    )
  }

  async get(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/payouts/{id}' },
      { id }
    )
  }

  /** F-12: the balance with the payout fee, `withdrawableBalance` and `maxPayoutAmount`. */
  async balance(params?: { currency?: string }): Promise<LedgerBalance> {
    return this.ctx.call({ httpMethod: 'GET', path: '/payouts/balance' }, { query: params })
  }

  /** Sent right away (F-12): the payout comes back PENDING, PROCESSING, COMPLETED or FAILED. */
  async create(payout: { amount: number }): Promise<Payout> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/payouts', hasBody: true },
      { body: payout }
    )
  }

  async cancel(id: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/payouts/{id}/cancel' },
      { id }
    )
  }
}

export class FeesResource {
  constructor(private ctx: SdkContext) {}

  async getCommunityFees(params?: {
    currency?: string
    countryCode?: string
  }): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/fees' },
      { query: params }
    )
  }

  async simulateFees(params: {
    amount: number
    paymentMethod: string
    installments?: number
    feePayer?: 'COMMUNITY' | 'CUSTOMER' | 'ORGANIZATION' | 'BUYER'
    /** F-43: who pays the card installment interest (BUYER by default). */
    installmentInterestPayer?: 'ORGANIZATION' | 'BUYER'
    currency?: string
    countryCode?: string
  }): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/fees/simulate', hasBody: true },
      { body: params }
    )
  }
}
